/**
 * Input sanitization utilities to protect against XSS and injection attacks
 * for fields that will be stored in S3
 */
/**
 * Escapes HTML entities to prevent XSS when displaying user input
 */
export var escapeHtml = function (input) {
    if (!input || typeof input !== 'string') {
        return '';
    }
    var htmlEscapes = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '/': '&#x2F;'
    };
    return input.replace(/[&<>"'/]/g, function (match) { return htmlEscapes[match]; });
};
/**
 * Sanitizes text input by removing potentially dangerous HTML/script tags
 * and normalizing whitespace
 */
export var sanitizeText = function (input) {
    if (!input || typeof input !== 'string') {
        return '';
    }
    return input
        // Remove HTML tags and script content
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<[^>]*>/g, '')
        // Remove JavaScript event handlers
        .replace(/\bon\w+\s*=/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/vbscript:/gi, '')
        .replace(/data:/gi, '')
        // Remove potentially dangerous CSS
        .replace(/expression\s*\(/gi, '')
        .replace(/url\s*\(/gi, '')
        // Normalize whitespace
        .replace(/\s+/g, ' ')
        .trim();
};
/**
 * Sanitizes text input with more strict rules for names and titles
 */
export var sanitizeName = function (input) {
    if (!input || typeof input !== 'string') {
        return '';
    }
    return input
        // Remove HTML tags
        .replace(/<[^>]*>/g, '')
        // Remove script content
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        // Remove JavaScript event handlers
        .replace(/\bon\w+\s*=/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/vbscript:/gi, '')
        // Remove only the most dangerous characters, preserve spaces and common punctuation
        .replace(/[<>{}[\]\\|`~]/g, '')
        // Normalize whitespace but preserve spaces
        .replace(/\s+/g, ' ')
        .trim();
};
/**
 * Sanitizes description text with more permissive rules for punctuation
 */
export var sanitizeDescription = function (input) {
    if (!input || typeof input !== 'string') {
        return '';
    }
    return input
        // Remove HTML tags
        .replace(/<[^>]*>/g, '')
        // Remove script content
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        // Remove JavaScript event handlers
        .replace(/\bon\w+\s*=/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/vbscript:/gi, '')
        .replace(/data:/gi, '')
        // Remove potentially dangerous CSS
        .replace(/expression\s*\(/gi, '')
        .replace(/url\s*\(/gi, '')
        // Allow common punctuation but remove dangerous ones
        .replace(/[<>{}[\]\\|`~]/g, '')
        // Normalize whitespace
        .replace(/\s+/g, ' ')
        .trim();
};
/**
 * Sanitizes wine type selection to ensure it's one of the allowed values
 */
export var sanitizeWineType = function (input) {
    var allowedTypes = [
        'Red Wine',
        'White Wine',
        'Rosé Wine',
        'Sparkling Wine'
    ];
    return allowedTypes.includes(input) ? input : 'Red Wine';
};
/**
 * Sanitizes region input
 */
export var sanitizeRegion = function (input) {
    return sanitizeName(input);
};
/**
 * Sanitizes alcohol content input
 */
export var sanitizeAlcoholContent = function (input) {
    if (!input || typeof input !== 'string') {
        return '13.0%';
    }
    // Allow format like "13.5%" or "13.5"
    var sanitized = input
        .replace(/<[^>]*>/g, '')
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/\bon\w+\s*=/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/vbscript:/gi, '')
        .replace(/[<>{}[\]\\|`~!@#$%^&*()+=]/g, '')
        .trim();
    // Validate format (number + optional %)
    var alcoholPattern = /^\d+(?:\.\d+)?%?$/;
    if (alcoholPattern.test(sanitized)) {
        return sanitized.endsWith('%') ? sanitized : sanitized + '%';
    }
    return '13.0%';
};
/**
 * Validates and sanitizes year input
 */
export var sanitizeYear = function (input) {
    var currentYear = new Date().getFullYear();
    var minYear = 1900;
    var maxYear = currentYear + 1;
    if (typeof input !== 'number' || isNaN(input)) {
        return currentYear;
    }
    if (input < minYear || input > maxYear) {
        return currentYear;
    }
    return input;
};
/**
 * Validates and sanitizes price input
 */
export var sanitizePrice = function (input) {
    if (typeof input !== 'number' || isNaN(input)) {
        return 0;
    }
    if (input < 0) {
        return 0;
    }
    // Round to 2 decimal places
    return Math.round(input * 100) / 100;
};
